import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Tooltip, message } from 'antd';
import {
  TabMenuContainer,
  SettingsHeading,
} from '../../SidebarMenu/styles';
import { DESIGN_AREA_SETTINGS_TAB } from '../../SidebarMenu/translationValues';
import FileSettings from './FileSettings';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import {
  CONTEXT_KEYS,
  TEMPLATE_TYPE,
  useArtboardContext,
} from '../../../context/complexArtboardContext';
import { ENTERPRISE_ENV } from '../../../utils/helper';
import CustomToolBar from '../complexVariables/CustomToolBar.js';
import ReactQuill from 'react-quill';
import { modules } from '../complexVariables/CustomToolBar.js';
import { checkSessionUserAccess } from '../../../utils/helper';
import { useSessionContext } from '../../../context/sessionContext.js';
import { TemplateDescription } from '../../SidebarMenu/styles';
import { RichTextEditorContainer } from '../complexVariables/styles.js';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { saveTemplateDescription } from '../../../utils/services/complexDocumentsAPI';
import { SharedFontControls } from './components/SharedFontControls';
import { AccessibilitySection } from './components/AccessibilitySection';
import { DisplayNameSection } from './components/DisplayNameSection';
import { CloneableSection } from './components/CloneableSection';
import { PpiSection } from './components/PpiSection';
import { PdfQualitySection } from './components/PdfQualitySection';
import CategoryInput from './CategoryInput.js';

const blockAndFlowTypes = [TEMPLATE_TYPE.BLOCK, TEMPLATE_TYPE.FLOW];

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

const DEFAULT_VALUE = [
  {
    contentType: 'PARAGRAPH',
    content: ''
  },
  {
    contentType: "LINE_BREAK",
    content: "\n"
  }
];

const RENDERING_TYPES = {
  DIGITAL: "digital",
  PRINT_RGB: "print_rgb",
  PRINT_CMYK: "print_cmyk",
};

const renderingTypeMap = {
  0: RENDERING_TYPES.DIGITAL,
  1: RENDERING_TYPES.PRINT_RGB,
  2: RENDERING_TYPES.PRINT_CMYK,
};

const ComplexSettingsTab = ({ CurrentDesign, fontListVisible, onFontToogle, ...props }) => {
  const { t } = useTranslation(NAMESPACES);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId, store] = useDocumentContext(documentIdSelector, ``);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexSettingsTab`);

  const isReadOnlySelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [isEditMode] = useArtboardContext(isReadOnlySelector, `ComplexSettingsTab`);

  const templateDescriptionSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_DESCRIPTION], []);
  const [templateDescription] = useDocumentContext(templateDescriptionSelector, `ComplexSettingsTab`);

  const readonlySelector = useCallback(store => store[CONTEXT_KEYS.READONLY_TEMPLATE], []);
  const [readOnly] = useArtboardContext(readonlySelector, 'ComplexSettingsTab');

  // Selectors for scopeType and scopeId calculation
  const groupIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID], []);
  const [groupId] = useDocumentContext(groupIdSelector, `ComplexSettingsTab`);

  const communicationIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID], []);
  const [communicationId] = useDocumentContext(communicationIdSelector, `ComplexSettingsTab`);

  // Helper function to calculate scopeType based on CurrentDesign
  const getScopeType = useCallback((design) => {
    if (design === 'document') {
      return 'communication';
    } else if (design === 'flow') {
      return 'flow-group';
    } else if (design === 'block') {
      return 'block-document';
    }
    return 'flow-group'; // default fallback
  }, []);

  // Helper function to calculate scopeId based on CurrentDesign
  const getScopeId = useCallback((design) => {
    if (design === 'document') {
      return communicationId;
    } else {
      return groupId;
    }
  }, [groupId, communicationId]);

  const editorRef = useRef();

  const [html, setHtml] = useState('');
  const [defaultValue, setDefaultValue] = useState(templateDescription);
  const [isdescriptionSaved, setIsdescriptionSaved] = useState(false);
  const [keyValue, setKeyValue] = useState(0);

  useEffect(()=>{
     setKeyValue(prev => prev + 1);
  },[])

  const quillContentChangeHandler = (html, delta, source, editor) => {
    const quillContents = editor.getContents()
    const converter = new QuillDeltaToHtmlConverter(quillContents.ops, {
      customCssClasses: {
        'ql-align-right': 'text-align: right;',
      },
      inlineStyles: true
    });
    const convertedHtml = converter.convert();
    setHtml(convertedHtml);
  }

  const onSave = async () => {
    setIsdescriptionSaved(false);
    const payload = {
      documentId: documentId,
      description: html
    }
    try {
      const response = await saveTemplateDescription(payload);
      setIsdescriptionSaved(true);
      if (response?.statusCode === 5500) {
        store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_DESCRIPTION]:html});
        setDefaultValue(html);
        message.success(
          t(DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVED.key, DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVED.value)
        );
      }
      else {
        message.error(
          t(DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.key, DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.value)
        );
      }
    }
    catch {
      message.error(
        t(DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.key, DESIGN_AREA_SETTINGS_TAB.DESCRIPTION_SAVE_FAILED.value)
      );
    }
  }

  return (
    <TabMenuContainer complexDesign disabled={isInteractiveDesign || !isEditMode}>
      <DisplayNameSection CurrentDesign={CurrentDesign} isEditMode={isEditMode} readOnly={readOnly} />

      {!readOnly ? <FileSettings scopeType={getScopeType(CurrentDesign)} scopeId={getScopeId(CurrentDesign)} /> : null}

      <CategoryInput templateId={documentId} isEditMode={isEditMode && !readOnly} />
      
      <PpiSection CurrentDesign={CurrentDesign} readOnly={readOnly} />
      
      {/*<div className='doc-settings-grouping settings-heading'>
        <SettingsHeading complexDesign className='doc-settings-width'>
          {t(DESIGN_AREA_SETTINGS_TAB.FONT_FAMILY.key, DESIGN_AREA_SETTINGS_TAB.FONT_FAMILY.value)}
        </SettingsHeading>
        <SettingsHeading complexDesign className='font-size-style'>
          {t(DESIGN_AREA_SETTINGS_TAB.FONT_SIZE.key, DESIGN_AREA_SETTINGS_TAB.FONT_SIZE.value)}
        </SettingsHeading>
      </div>*/}

        <SharedFontControls
          CurrentDesign={CurrentDesign}
          fontListVisible={fontListVisible}
          onFontToogle={onFontToogle}
          LabelComponent={SettingsHeading}
        />

      <AccessibilitySection CurrentDesign={CurrentDesign} readOnly={readOnly} />

      {checkSessionUserAccess(userDetails, 'master') && !readOnly &&
        <>
          <SettingsHeading complexDesign>
            {t(DESIGN_AREA_SETTINGS_TAB.TEMPLATE_DESCRIPTION_LABEL.key, DESIGN_AREA_SETTINGS_TAB.TEMPLATE_DESCRIPTION_LABEL.value)}{' '}
          </SettingsHeading>
          <RenderTemplateDescription t={t} defaultValue={defaultValue} quillContentChangeHandler={quillContentChangeHandler} onSave={onSave} editorRef={editorRef} isdescriptionSaved={isdescriptionSaved} keyValue={keyValue} />
        </>
      }
      
      <CloneableSection CurrentDesign={CurrentDesign} isInteractiveDesign={isInteractiveDesign} readOnly={readOnly} />
      
      <PdfQualitySection readOnly={readOnly} />
    </TabMenuContainer>
  );
};

const RenderTemplateDescription = ({ t, defaultValue, quillContentChangeHandler, onSave, editorRef, isdescriptionSaved, keyValue }) => {
  return (
      <RichTextEditorContainer isTemplateDescription={true}>
        {keyValue ? <div key={keyValue} style={{ marginTop: '6px', border: '2px solid white', transition: 'opacity 0.9s ease-in-out', borderRadius: '4px', background: 'white', borderRadius: '10px', padding: '2px' }}>
          <CustomToolBar t={t} isTemplateDescription={true} className='custom-picker' varName={'html-ixck'} onSave={onSave} editorRef={editorRef} isdescriptionSaved={isdescriptionSaved} />
          <ReactQuill style={{ backgroundColor: 'white' }}
            className='custom-picker'
            ref={editorRef}
            defaultValue={defaultValue}
            modules={modules('html-ixck')}
            onChange={(html, delta, source, editor) => { quillContentChangeHandler(html, delta, source, editor) }}
          />
        </div> : null}
      </RichTextEditorContainer>
  )
}

export default ComplexSettingsTab;


